<template>
  <v-dialog
    v-model="show"
    :contentClass="getContentClasses"
    :fullscreen="isFullScreen"
    :persistent="persistent"
    :width="dialogWidth"
    :minHeight="minHeight"
    :maxHeight="maxHeight"
    transition="dialog-bottom-transition"
    elevation="0"
    scrollable
    data-cc="global-modal-dialog"
    @keydown.enter="confirmDialog"
    @keydown.esc="closeDialog"
    @click:outside="closeDialog"
  >
    <v-card
      elevation="0"
      :data-cc="dialogTestId || undefined"
      :data-status="dialogStatus || undefined"
    >
      <v-card-item v-if="showHeader">
        <slot name="header">
          <v-card-title class="d-flex align-center dialog-header ga-2">
            <slot name="headerInnerPrepend" />
            <span
              v-if="headerText"
              v-sanitize="headerText"
              class="text-h5"
              data-cc="global-modal-dialog-header"
            />

            <v-spacer />

            <slot name="headerInnerAppend" />

            <v-icon-btn
              v-if="closable"
              icon="$close"
              variant="text"
              color="secondary"
              data-cc="global-modal-dialog-header-close"
              @click="closeDialog"
            />
          </v-card-title>
        </slot>
      </v-card-item>

      <div v-if="$slots.appendHeader" class="mx-6 mb-6">
        <slot name="appendHeader" />
      </div>

      <v-divider v-if="showDivider" />
      <v-progress-linear v-if="loading" :height="2" indeterminate />

      <v-card-text
        :class="{
          'pt-0': !showDivider && showHeader,
          'mb-6 pb-0': !showFooter
        }"
      >
        <div class="bmDialog__content">
          <slot />
        </div>
      </v-card-text>

      <slot v-if="showFooter" name="footer">
        <v-card-actions>
          <slot name="actions" />

          <BMButton
            v-if="backButton"
            data-cc="global-modal-dialog-back"
            :title="backLabel || $t('generic.back')"
            variant="outlined"
            @clicked="$emit('back')"
          />

          <BMButton
            v-if="!hideSubmitButton"
            data-cc="global-modal-dialog-save"
            :disabled="disableConfirm"
            :title="submitText || $t('generic.save')"
            @clicked="confirmDialog"
          />
        </v-card-actions>
      </slot>
    </v-card>
  </v-dialog>
</template>

<script>
import BMButton from '@/components/common/utilities/BMButton';

export default {
  name: 'BMDialog',
  components: {
    BMButton
  },
  props: {
    backButton: {
      type: Boolean,
      default: false
    },
    backLabel: {
      type: String,
      default: ''
    },
    submitText: {
      type: String,
      default: ''
    },
    contentClass: {
      type: String,
      default: ''
    },
    persistent: {
      type: Boolean,
      default: true
    },
    showDialog: {
      type: Boolean,
      default: false
    },
    headerText: {
      type: String,
      default: ''
    },
    disableConfirm: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    closable: {
      type: Boolean,
      default: true
    },
    hideSubmitButton: {
      type: Boolean,
      default: false
    },
    largeDialog: {
      type: Boolean,
      default: false
    },
    fullscreen: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    showDivider: {
      type: Boolean,
      default: true
    },
    dialogTestId: {
      type: String,
      default: ''
    },
    dialogStatus: {
      type: [String, null],
      default: null
    },
    width: {
      type: String,
      default: ''
    },
    minHeight: {
      type: [String, Number],
      required: false
    },
    maxHeight: {
      type: [String, Number],
      default: '80vh'
    }
  },
  methods: {
    closeDialog() {
      if (this.closable) this.$emit('closed');
    },
    confirmDialog() {
      if (!this.disableConfirm) this.$emit('confirmed');
    },
    setFocusOnDialog() {
      document.activeElement?.blur();
    }
  },
  mounted() {
    this.setFocusOnDialog();
  },
  computed: {
    isFullScreen() {
      return this.isMobileVersion && this.fullscreen;
    },
    isMobileVersion() {
      return this.$vuetify.display.xs;
    },
    show() {
      return this.showDialog;
    },
    dialogWidth() {
      if (this.isFullScreen) return '100%';
      if (this.width) return this.width;
      const sizes = {
        md: 70,
        lg: 50,
        xl: 40,
        xxl: 40
      };
      const width =
        sizes[this.$vuetify.display.name] + (this.largeDialog ? 15 : 0);
      return `${width}vw`;
    },
    getContentClasses() {
      return `overflow-y-auto ${this.contentClass} ${
        this.isMobileVersion ? 'mobile-dialog' : ''
      }`;
    }
  },
  watch: {
    showDialog() {
      this.setFocusOnDialog();
    }
  }
};
</script>

<style lang="scss" scoped>
.v-dialog--scrollable > .v-overlay__content > .v-card > .v-card-text,
.v-dialog--scrollable > .v-overlay__content > form > .v-card > .v-card-text {
  scrollbar-gutter: stable both-edges;

  .bmDialog__content {
    margin-inline: calc(var(--v-scrollbar-width, 6px) * -1);
  }
}
</style>
